import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Select,
  InputNumber,
  message,
  Spin,
  Row,
  Col,
  Divider,
  Avatar,
} from "antd";
import {
  ArrowLeftOutlined,
  UploadOutlined,
  SaveOutlined,
  BankOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";

const { TextArea } = Input;
const { Option } = Select;

const FormWrapper = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  .back-button {
    margin-bottom: 16px;
    color: #1890ff;
    font-weight: 500;
  }

  .form-card {
    background: #fff;
    border-radius: 12px;
    padding: 32px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .form-title {
    font-size: 24px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 24px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }

  .upload-wrapper {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 24px;
  }

  .logo-preview {
    width: 120px;
    height: 120px;
    border-radius: 12px;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .logo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .banner-preview {
    width: 100%;
    height: 150px;
    border-radius: 12px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    margin-bottom: 16px;
    overflow: hidden;
  }

  .banner-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .required-badge {
    color: #ff4d4f;
    font-size: 12px;
    margin-left: 4px;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #f0f0f0;
  }
`;

const industryOptions = [
  "Information Technology",
  "Software Development",
  "Consulting",
  "Finance & Banking",
  "Healthcare",
  "E-commerce",
  "Manufacturing",
  "Telecommunications",
  "Media & Entertainment",
  "Education",
  "Real Estate",
  "Automotive",
  "Retail",
  "Energy & Utilities",
  "Transportation & Logistics",
  "Other",
];

const employeeCountOptions = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "5000+",
];

const CompanyDetails = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  useEffect(() => {
    fetchOrganization();
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.GET_MY_PROFILE, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.user) {
        setUserRole(response.data.user.role?.toLowerCase());
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const fetchOrganization = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.GET_MY_ORGANIZATION, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.organization) {
        const org = response.data.organization;
        setOrganization(org);
        setLogoUrl(org.logo);
        setBannerUrl(org.coverBanner);
        form.setFieldsValue({
          name: org.name,
          industry: org.industry,
          website: org.website,
          gstNumber: org.gstNumber,
          employeeCount: org.employeeCount,
          summary: org.summary,
          address: org.address,
          city: org.city,
          state: org.state,
          country: org.country,
          pincode: org.pincode,
          phone: org.phone,
          email: org.email,
          foundedYear: org.foundedYear,
          linkedin: org.socialLinks?.linkedin,
          twitter: org.socialLinks?.twitter,
          facebook: org.socialLinks?.facebook,
          instagram: org.socialLinks?.instagram,
        });
      } else {
        const orgName = localStorage.getItem("userOrganization");
        if (orgName) {
          form.setFieldsValue({ name: orgName });
        }
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
      const orgName = localStorage.getItem("userOrganization");
      if (orgName) {
        form.setFieldsValue({ name: orgName });
      } else {
        message.error("Failed to load company details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem("authToken");
      const requestResponse = await axios.post(
        API_CONST.UPLOAD_REQUEST_URL,
        {
          fileName: file.name,
          contentType: file.type,
          folder: "organization-logos",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (requestResponse.data.uploadUrl) {
        await axios.put(requestResponse.data.uploadUrl, file, {
          headers: { "Content-Type": file.type },
        });
        setLogoUrl(requestResponse.data.objectPath);
        message.success("Logo uploaded successfully");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      message.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
    return false;
  };

  const handleBannerUpload = async (file) => {
    setUploadingBanner(true);
    try {
      const token = localStorage.getItem("authToken");
      const requestResponse = await axios.post(
        API_CONST.UPLOAD_REQUEST_URL,
        {
          fileName: file.name,
          contentType: file.type,
          folder: "organization-banners",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (requestResponse.data.uploadUrl) {
        await axios.put(requestResponse.data.uploadUrl, file, {
          headers: { "Content-Type": file.type },
        });
        setBannerUrl(requestResponse.data.objectPath);
        message.success("Banner uploaded successfully");
      }
    } catch (error) {
      console.error("Error uploading banner:", error);
      message.error("Failed to upload banner");
    } finally {
      setUploadingBanner(false);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        ...values,
        logo: logoUrl,
        coverBanner: bannerUrl,
        socialLinks: {
          linkedin: values.linkedin,
          twitter: values.twitter,
          facebook: values.facebook,
          instagram: values.instagram,
        },
      };

      delete payload.linkedin;
      delete payload.twitter;
      delete payload.facebook;
      delete payload.instagram;

      await axios.put(API_CONST.UPDATE_MY_ORGANIZATION, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success("Company details updated successfully");
      navigate(-1);
    } catch (error) {
      console.error("Error updating organization:", error);
      message.error("Failed to update company details");
    } finally {
      setSaving(false);
    }
  };

  const isPartner = userRole === "partner";

  if (loading) {
    return (
      <FormWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <p style={{ marginTop: 16 }}>Loading company details...</p>
        </div>
      </FormWrapper>
    );
  }

  return (
    <FormWrapper>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        className="back-button"
      >
        Back
      </Button>

      <Card className="form-card">
        <h2 className="form-title">Company Details</h2>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="section-title">Company Logo & Banner</div>
          
          <div className="upload-wrapper">
            <div className="logo-preview">
              {logoUrl ? (
                <Avatar size={100} src={logoUrl} />
              ) : (
                <Avatar size={100} icon={<BankOutlined />} style={{ backgroundColor: "#1890ff" }} />
              )}
            </div>
            <Upload
              beforeUpload={handleLogoUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={uploadingLogo ? <LoadingOutlined /> : <UploadOutlined />} loading={uploadingLogo}>
                {logoUrl ? "Change Logo" : "Upload Logo"}
              </Button>
            </Upload>
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>Recommended: 200x200 px</span>
          </div>

          <div className="banner-preview">
            {bannerUrl && <img src={bannerUrl} alt="Banner" />}
          </div>
          <Upload
            beforeUpload={handleBannerUpload}
            showUploadList={false}
            accept="image/*"
          >
            <Button icon={uploadingBanner ? <LoadingOutlined /> : <UploadOutlined />} loading={uploadingBanner}>
              {bannerUrl ? "Change Cover Banner" : "Upload Cover Banner"}
            </Button>
          </Upload>
          <span style={{ color: "#8c8c8c", fontSize: 12, marginLeft: 12 }}>Optional - Recommended: 1200x300 px</span>

          <Divider />

          <div className="section-title">Basic Information</div>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Company Name"
                rules={[{ required: true, message: "Company name is required" }]}
              >
                <Input placeholder="Enter company name" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="industry"
                label={<span>Industry {isPartner && <span className="required-badge">*</span>}</span>}
                rules={[{ required: true, message: "Industry is required" }]}
              >
                <Select placeholder="Select industry">
                  {industryOptions.map((industry) => (
                    <Option key={industry} value={industry}>
                      {industry}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="website"
                label={<span>Website {isPartner && <span className="required-badge">*</span>}</span>}
                rules={[{ required: true, message: "Website is required" }]}
              >
                <Input placeholder="https://www.example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="gstNumber"
                label={<span>GST/Registration ID {isPartner && <span className="required-badge">*</span>}</span>}
                rules={isPartner ? [{ required: true, message: "GST Number is required for partners" }] : []}
              >
                <Input placeholder="Enter GST/Registration ID" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="employeeCount"
                label={<span>Employee Count {isPartner && <span className="required-badge">*</span>}</span>}
                rules={isPartner ? [{ required: true, message: "Employee count is required for partners" }] : []}
              >
                <Select placeholder="Select employee count range">
                  {employeeCountOptions.map((count) => (
                    <Option key={count} value={count}>
                      {count}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="foundedYear" label="Founded Year">
                <InputNumber
                  placeholder="e.g. 2010"
                  min={1900}
                  max={new Date().getFullYear()}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="summary"
                label={<span>Company Summary {isPartner && <span className="required-badge">*</span>}</span>}
                rules={isPartner ? [{ required: true, message: "Company summary is required for partners" }] : []}
              >
                <TextArea rows={4} placeholder="Brief description about your company..." />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <div className="section-title">Contact Information</div>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="email" label="Email">
                <Input placeholder="contact@company.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="+91 9876543210" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="address"
                label={<span>Address {isPartner && <span className="required-badge">*</span>}</span>}
                rules={isPartner ? [{ required: true, message: "Address is required for partners" }] : []}
              >
                <Input placeholder="Street address" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="city" label="City">
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="state" label="State">
                <Input placeholder="State" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="pincode" label="Pincode">
                <Input placeholder="Pincode" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="country" label="Country">
                <Input placeholder="Country" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <div className="section-title">Social Links</div>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="linkedin" label="LinkedIn">
                <Input placeholder="https://linkedin.com/company/..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="twitter" label="Twitter">
                <Input placeholder="https://twitter.com/..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="facebook" label="Facebook">
                <Input placeholder="https://facebook.com/..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="instagram" label="Instagram">
                <Input placeholder="https://instagram.com/..." />
              </Form.Item>
            </Col>
          </Row>

          <div className="form-actions">
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              style={{ backgroundColor: "#00d9a9", borderColor: "#00d9a9" }}
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Card>
    </FormWrapper>
  );
};

export default CompanyDetails;
